<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
    modelValue: {
        type: [Object, String, null], // Object for File
        default: null
    },
    label: {
        type: String,
        default: null
    },
    error: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['update:modelValue']);
const processing = ref(false);
const localError = ref(null);

// Local object URL for previewing newly selected files
const filePreview = ref(null);

const previewUrl = computed(() => {
    if (filePreview.value) {
        return filePreview.value;
    }
    if (typeof props.modelValue === 'string' && props.modelValue !== '') {
        return props.modelValue;
    }
    return null;
});

const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Show local preview immediately
    filePreview.value = URL.createObjectURL(file);
    
    // Upload to S3 via Vapor
    processing.value = true;
    localError.value = null;
    try {
        const response = await window.Vapor.store(file);
        emit('update:modelValue', response.key);
    } catch (error) {
        console.error('Vapor upload failed:', error);
        localError.value = 'Das Hochladen des Bildes ist fehlgeschlagen.';
        if (filePreview.value) {
            URL.revokeObjectURL(filePreview.value);
            filePreview.value = null;
        }
        emit('update:modelValue', null);
    } finally {
        processing.value = false;
        event.target.value = '';
    }
};

const clearImage = () => {
    if (filePreview.value) {
        URL.revokeObjectURL(filePreview.value);
        filePreview.value = null;
    }
    emit('update:modelValue', null);
};

// Clean up object URL to prevent memory leaks
onBeforeUnmount(() => {
    if (filePreview.value) {
        URL.revokeObjectURL(filePreview.value);
    }
});
</script>

<template>
    <div class="flex flex-col gap-2 w-full">
        <label v-if="label" class="font-bold text-gray-600">{{ label }}</label>
        
        <!-- Preview State -->
        <div v-if="previewUrl" class="relative group w-36 h-48 rounded-xl overflow-hidden border border-gray-200 shadow-sm flex-shrink-0">
            <img :src="previewUrl" class="w-full h-full object-cover" alt="Image Preview" />
            
            <!-- Hover Overlay -->
            <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" @click.prevent="clearImage" class="bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg" title="Bild entfernen">
                    <i class="pi pi-trash" />
                </button>
            </div>
        </div>

        <!-- Upload State -->
        <div v-else class="flex items-center justify-start w-full">
            <label class="flex flex-col items-center justify-center w-36 h-48 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors group flex-shrink-0">
                <div v-if="processing" class="flex flex-col items-center">
                    <i class="pi pi-spin pi-spinner text-2xl text-gray-400 mb-2" />
                    <p class="text-[10px] text-gray-400">Hochladen...</p>
                </div>
                <template v-else>
                    <div class="w-10 h-10 mb-2 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                        <i class="pi pi-cloud-upload text-xl text-gray-500" />
                    </div>
                    <p class="text-xs text-gray-600 font-semibold leading-tight">Klicken zum Hochladen</p>
                </template>
                <input type="file" class="hidden" accept="image/png, image/jpeg, image/jpg, image/webp" @change="handleFileChange" />
            </label>
        </div>

        <!-- Error Message -->
        <p v-if="error || localError" class="text-xs text-red-500 mt-1">
            {{ error || localError }}
        </p>
    </div>
</template>
